/**
 * SaveQuoteDialog Component
 * Modal for saving a calculated quote with customer information
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, User, Mail, Phone, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { useToast } from './ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { BoxSpecifications, CostBreakdown } from '../types';
import { saveQuote as apiSaveQuote } from '../utils/api';

interface SaveQuoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  specifications: BoxSpecifications;
  breakdown: CostBreakdown;
  vendorProfit?: number;
  onSuccess?: (quoteId: string) => void;
  variants?: Array<{ quantity: number; breakdown: CostBreakdown }>;
  primaryQuantity?: number;
  marginData?: {
    // Agent margin data
    agentMarginPercent?: number;
    agentCompanyProfit?: number;
    finalTotalWithAgentMargin?: number;
    costPerUnitWithAgentMargin?: number;
    // Admin margin data
    manufacturerMarginPercent?: number;
    companyMarginPercent?: number;
    manufacturerProfitAmount?: number;
    adminCompanyProfitAmount?: number;
    adminTotalWithProfit?: number;
    adminCostPerUnitWithProfit?: number;
  };
  draftId?: string;
}

interface CustomerContact {
  email?: string;
  phone?: string;
}

export const SaveQuoteDialog: React.FC<SaveQuoteDialogProps> = ({
  isOpen,
  onClose,
  specifications,
  breakdown,
  vendorProfit = 0,
  onSuccess,
  variants,
  primaryQuantity,
  marginData,
  draftId,
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');

  const handleSave = async () => {
    // Prevent multiple submissions
    if (loading) {
      return;
    }

    // Validate required fields
    if (!customerName.trim()) {
      setError('Customer name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build customer contact object
      const customerContact: CustomerContact = {};
      if (customerEmail) customerContact.email = customerEmail;
      if (customerPhone) customerContact.phone = customerPhone;

      // Prepare quote data
      const isAgent = user?.role === 'agent' || user?.role === 'user';
      const quoteData: Record<string, unknown> = {
        customerName: customerName.trim(),
        customerContact,
        notes: notes.trim(),
        currency: 'PKR',
        taxPercent: 0,
        input: specifications,
        breakdown,
        pricingVersion: breakdown.metadata?.pricingVersion || '1.0.0',
        // vendorProfit: for admins this is manufacturer profit; for agents this should reflect agent margin percent if provided
        vendorProfit: isAgent && marginData ? marginData.agentMarginPercent : vendorProfit,
        // Explicit totals with profit (server expects these keys)
        unitCostWithProfit: breakdown.costPerUnit,
        totalWithProfit: breakdown.total,
        // Persist 2-tier profit fields for traceability
        baseManufacturerProfit: (breakdown as Record<string, unknown>).baseManufacturerProfit,
        baseManufacturerProfitAmount: (breakdown as Record<string, unknown>).baseManufacturerProfitAmount,
      };

      // Include margin data based on user role
      if (marginData) {
        // Agent margin data
        if (marginData.agentMarginPercent !== undefined) {
          quoteData.agentMarginPercent = marginData.agentMarginPercent;
          quoteData.agentCompanyProfit = marginData.agentCompanyProfit;
          quoteData.finalTotalWithAgentMargin = marginData.finalTotalWithAgentMargin;
          quoteData.costPerUnitWithAgentMargin = marginData.costPerUnitWithAgentMargin;
          // Override totals with the agent-calculated values (server reads these keys)
          quoteData.unitCostWithProfit = marginData.costPerUnitWithAgentMargin;
          quoteData.totalWithProfit = marginData.finalTotalWithAgentMargin;
          // Align breakdown to reflect agent margin at save time
          const cloned = JSON.parse(JSON.stringify(breakdown));
          cloned.vendorProfit = marginData.agentMarginPercent;
          cloned.vendorProfitAmount = marginData.agentCompanyProfit;
          cloned.total = marginData.finalTotalWithAgentMargin;
          cloned.costPerUnit = marginData.costPerUnitWithAgentMargin;
          quoteData.breakdown = cloned;
        }

        // Admin margin data
        if (marginData.manufacturerMarginPercent !== undefined) {
          // Save admin slider values as primary vendorProfit and companyProfit fields
          quoteData.vendorProfit = marginData.manufacturerMarginPercent;
          quoteData.vendorProfitAmount = marginData.manufacturerProfitAmount;
          quoteData.companyProfit = marginData.companyMarginPercent;
          quoteData.companyProfitAmount = marginData.adminCompanyProfitAmount;

          // Also save to admin-specific fields for backward compatibility
          quoteData.manufacturerMarginPercent = marginData.manufacturerMarginPercent;
          quoteData.companyMarginPercent = marginData.companyMarginPercent;
          quoteData.manufacturerProfitAmount = marginData.manufacturerProfitAmount;
          quoteData.adminCompanyProfitAmount = marginData.adminCompanyProfitAmount;
          quoteData.adminTotalWithProfit = marginData.adminTotalWithProfit;
          quoteData.adminCostPerUnitWithProfit = marginData.adminCostPerUnitWithProfit;
          // Override totals with the admin-calculated values
          quoteData.unitCostWithProfit = marginData.adminCostPerUnitWithProfit;
          quoteData.totalWithProfit = marginData.adminTotalWithProfit;
          // Align breakdown to reflect admin margins at save time
          const cloned = JSON.parse(JSON.stringify(breakdown));
          cloned.vendorProfit = marginData.manufacturerMarginPercent;
          cloned.vendorProfitAmount = marginData.manufacturerProfitAmount;
          cloned.companyMarginPercent = marginData.companyMarginPercent; // Fix: ensure breakdown has correct company margin %
          cloned.adminCompanyProfitAmount = marginData.adminCompanyProfitAmount; // Also sync the amount
          cloned.total = marginData.adminTotalWithProfit;
          cloned.costPerUnit = marginData.adminCostPerUnitWithProfit;
          quoteData.breakdown = cloned;
        }
      }

      // Include multi-quantity variants if provided
      if (variants && variants.length > 0) {
        // Apply admin/agent margin data to each variant's breakdown
        const updatedVariants = variants.map(v => {
          const variantBreakdown = JSON.parse(JSON.stringify(v.breakdown));

          // Apply margin data to variant breakdown based on user role
          if (marginData) {
            // Agent margin data
            if (marginData.agentMarginPercent !== undefined) {
              variantBreakdown.vendorProfit = marginData.agentMarginPercent;
              variantBreakdown.vendorProfitAmount = marginData.agentCompanyProfit;
              // Note: Total and cost per unit are already correctly calculated per variant
            }

            // Admin margin data
            if (marginData.manufacturerMarginPercent !== undefined) {
              variantBreakdown.vendorProfit = marginData.manufacturerMarginPercent;
              variantBreakdown.vendorProfitAmount = marginData.manufacturerProfitAmount;
              variantBreakdown.companyMarginPercent = marginData.companyMarginPercent;
              variantBreakdown.adminCompanyProfitAmount = marginData.adminCompanyProfitAmount;
            }
          }

          return { quantity: v.quantity, breakdown: variantBreakdown };
        });

        quoteData.variants = updatedVariants;
        quoteData.primaryQuantity = primaryQuantity || variants[0].quantity;
      }

      if (draftId) {
        quoteData.draftId = draftId;
      }

      // Use centralized API function that handles authentication headers
      const result = await apiSaveQuote(quoteData);

      if (!result.success) {
        throw new Error(result.error || 'Failed to save quote');
      }

      // Success! Show toast notification
      showToast({
        type: 'success',
        title: 'Quote Saved Successfully',
        message: `Quote ${result.data?.id} has been saved and is valid for 30 days.`,
      });
      
      // Call success callback if provided
      if (onSuccess && result.data?.id) {
        onSuccess(result.data.id);
      }

      // Close dialog
      onClose();
      
      // Navigate to quotes page
      setTimeout(() => {
        navigate('/quotes');
      }, 100);
      
    } catch (err) {
      console.error('Error saving quote:', err);
      setError(err instanceof Error ? err.message : 'Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setNotes('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Save Quote
          </DialogTitle>
          <DialogDescription>
            Save this quote for future reference. It will be valid for 30 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer Name (Required) */}
          <div className="space-y-2">
            <Label htmlFor="customerName" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Customer Name *
            </Label>
            <Input
              id="customerName"
              type="text"
              placeholder="Enter customer name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={loading}
              required
              className={error && !customerName ? 'border-destructive' : ''}
            />
          </div>

          {/* Customer Email (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="customerEmail" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Customer Email
            </Label>
            <Input
              id="customerEmail"
              type="email"
              placeholder="customer@example.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Customer Phone (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="customerPhone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Customer Phone
            </Label>
            <Input
              id="customerPhone"
              type="tel"
              placeholder="+1 234 567 8900"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Notes (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes
            </Label>
            <textarea
              id="notes"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={3}
              placeholder="Add any additional notes about this quote..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Quote Summary */}
          <div className="p-4 rounded-lg space-y-2 bg-muted border border-border">
            <h4 className="font-semibold text-sm text-card-foreground">Quote Summary</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Quantity:</div>
              <div className="font-medium text-foreground">{(primaryQuantity || specifications.quantity).toLocaleString()} units</div>
              
              <div className="text-muted-foreground">Material:</div>
              <div className="font-medium capitalize text-foreground">
                {specifications.material} {specifications.gsm} GSM
              </div>
              
              <div className="text-muted-foreground">Total Cost:</div>
              <div className="font-medium text-foreground">
                PKR {(
                  marginData?.adminTotalWithProfit ??
                  marginData?.finalTotalWithAgentMargin ??
                  breakdown.total
                ).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>

              <div className="text-muted-foreground">Cost Per Unit:</div>
              <div className="font-medium text-foreground">
                PKR {(
                  marginData?.adminCostPerUnitWithProfit ??
                  marginData?.costPerUnitWithAgentMargin ??
                  breakdown.costPerUnit
                ).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>

              {/* Manufacturer Profit - show admin's current setting or base company setting */}
              {(() => {
                const isAdmin = user?.role === 'admin' || user?.role === 'company_admin' || user?.role === 'superadmin';

                if (isAdmin && marginData?.manufacturerMarginPercent !== undefined) {
                  // Admin mode: Show the admin's current manufacturer profit setting
                  return (
                    <>
                      <div className="text-muted-foreground">Manufacturer Profit:</div>
                      <div className="font-medium text-foreground">
                        {marginData.manufacturerMarginPercent}% (PKR {(marginData.manufacturerProfitAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </div>
                    </>
                  );
                } else if ((breakdown as Record<string, unknown>).baseManufacturerProfit !== undefined) {
                  // Agent mode or fallback: Show base company setting
                  return (
                    <>
                      <div className="text-muted-foreground">Base Manufacturer Profit:</div>
                      <div className="font-medium text-foreground">
                        {(breakdown as Record<string, unknown>).baseManufacturerProfit}% (PKR {((breakdown as Record<string, unknown>).baseManufacturerProfitAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </div>
                    </>
                  );
                }
                return null;
              })()}

              {/* Agent Margin (for agent quotes) */}
              {marginData && marginData.agentMarginPercent !== undefined && (
                <>
                  <div className="text-muted-foreground">Agent Margin:</div>
                  <div className="font-medium text-foreground">
                    {marginData.agentMarginPercent}% (PKR {(marginData.agentCompanyProfit ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </div>
                </>
              )}

              {/* Company Margin (for admin quotes) */}
              {marginData && marginData.companyMarginPercent !== undefined && marginData.companyMarginPercent > 0 && (
                <>
                  <div className="text-muted-foreground">Company Profit:</div>
                  <div className="font-medium text-foreground">
                    {marginData.companyMarginPercent}% (PKR {(marginData.adminCompanyProfitAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </div>
                </>
              )}

              {/* Vendor Profit - only show for legacy compatibility or when not already shown above */}
              {(() => {
                const isAgent = user?.role === 'agent' || user?.role === 'user';
                const isAdmin = user?.role === 'admin' || user?.role === 'company_admin' || user?.role === 'superadmin';

                // Don't show vendor profit for admin users since we already show manufacturer profit above
                if (isAdmin && marginData?.manufacturerMarginPercent !== undefined) {
                  return null;
                }

                // Don't show vendor profit for agents when we have agent margin data
                if (isAgent && marginData?.agentMarginPercent !== undefined) {
                  return null;
                }

                // Show for legacy cases when no specific margin data is available
                if (vendorProfit > 0) {
                  return (
                    <>
                      <div className="text-muted-foreground">Vendor Profit:</div>
                      <div className="font-medium text-foreground">{vendorProfit}%</div>
                    </>
                  );
                }

                return null;
              })()}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !customerName.trim()}
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {loading ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Quote
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
